import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { Box } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { DocumentSectionType } from "constants/documents";
import { useActionHook } from "hooks";
import { useDocumentsActions, usePickUserPermissions, useUserIdSelector, useUserRoleSelector } from "store";
import { Document as DocumentType, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { Content, DocumentProps } from "../Content/Content";
import { SharePopup, SharePopupProps } from "../Share/Pop";

import "./Document.styles.scss";

interface DocumentComponentProps {
  onClose?: () => void;
}

export const Document: React.FC<DocumentComponentProps> = ({ onClose }) => {
  const history = useHistory();
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [popupState, setPopupState] = useState<{ open: boolean; sections: number[] }>({ open: false, sections: [] });

  const { getDocumentContentAction, shareDocumentSectionAction } = useDocumentsActions();

  const { document_id } = useParams<{ document_id: string }>();
  const docId = Number(document_id);
  const userId = useUserIdSelector();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const documentSectionId = searchParams.get("documentSectionId");
  const containerRef = useRef<HTMLDivElement>(null);

  const dealsPermission = usePickUserPermissions("deals");
  const role = useUserRoleSelector();
  const isReadOnlyUser =
    role?.name === RoleName.Guest ||
    role?.name === RoleName.Viewer ||
    role?.name === RoleName.TeamViewer ||
    dealsPermission === UserPermissionType.VIEWER ||
    dealsPermission === UserPermissionType.NO_ACCESS;

  const {
    data: document,
    setData,
    action: getDocumentContent,
    loading
  } = useActionHook(getDocumentContentAction, {
    defaultLoadingState: true,
    onSuccess: (data) => {
      if (isReadOnlyUser) return;
      // focus first empty text section when document is loaded
      // use timeout to ensure the container is rendered
      setTimeout(() => {
        const { current } = containerRef;
        if (!current) return;

        const firstTextSection = data.sections.find((section) => section.type === DocumentSectionType.Text);
        if (!firstTextSection) return;

        const sectionElement = current.querySelector(`[data-section-id="${firstTextSection.id}"]`);
        if (!sectionElement) return;

        const editableContent = sectionElement.querySelector(".tiptap") as HTMLElement;
        if (!editableContent) return;

        editableContent.focus();
      }, 100);
    }
  });

  useEffect(() => {
    if (!docId) return;
    getDocumentContent(docId);
  }, [docId]);

  // Scroll to the section if we navigate from deal meeting folder
  useEffect(() => {
    if (document && documentSectionId) {
      const sectionId = Number(documentSectionId);

      const sectionElement = window.document.querySelector(`[data-section-id="${sectionId}"]`) as HTMLElement;

      if (sectionElement) {
        setTimeout(() => {
          sectionElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }, 100);
      }

      // Remove documentSectionId from URL after scrolling
      const newSearchParams = new URLSearchParams(search);
      newSearchParams.delete("documentSectionId");
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch ? `?${newSearch}` : "";

      history.replace(`${window.location.pathname}${newUrl}`);
    }
  }, [document, documentSectionId, search, history]);

  const handleShare: DocumentProps["onShowSharePopup"] = (sectionId) => {
    setPopupState({ open: true, sections: [sectionId] });
  };

  const handleSelectSection: DocumentProps["onSelectSection"] = (section, selected) => {
    const selectedSections = new Set(popupState.sections);
    selected ? selectedSections.add(section) : selectedSections.delete(section);
    setPopupState({ open: false, sections: Array.from(selectedSections) });
  };

  const handleClosePopup = () => {
    setPopupState({ open: false, sections: popupState.sections });
  };

  const handleInvite: SharePopupProps["onInvite"] = async (users, type, message) => {
    if (!document) return;
    const userIds = users.map((user) => user.id);
    const sections = await shareDocumentSectionAction({
      documentId: document.id,
      sections: popupState.sections,
      type,
      users: userIds,
      message
    });
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections?.map((section) => {
          const updatedSection = sections.find((item) => item.id === section.id);
          return updatedSection ? { ...section, ...updatedSection } : section;
        })
      };
    });
  };

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 150 }}>
      {document && userId && (
        <Content
          document={document}
          setDocument={setData as React.Dispatch<React.SetStateAction<DocumentType<true>>>}
          onShowSharePopup={handleShare}
          onSelectSection={handleSelectSection}
          selectedSections={popupState.sections}
          setSelectionMode={setSelectionMode}
          selectionMode={selectionMode}
          userId={userId}
          containerRef={containerRef}
          onClose={onClose}
        />
      )}
      <Box className="documents">
        <SharePopup onClose={handleClosePopup} open={popupState.open} onInvite={handleInvite} />
      </Box>
    </AsyncContentMui>
  );
};
